"use client";

import React, { useEffect, useRef, useState } from "react";
import "./Product_details_swiper.css";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faHeart as faHeartSolid,
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";

function Product_details_swiper({ product }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  const mainSwiperRef = useRef(null);
  const thumbnailSwiperRef = useRef(null);

  const [zoomPosition, setZoomPosition] = useState({
    x: 50,
    y: 50,
  });

  const productImages = [
    ...(product?.image ? [{ image: product.image, id: "main" }] : []),
    ...(product?.Product_Images || []),
  ];

  /* =========================
     RESET WHEN PRODUCT CHANGES
  ========================= */

  useEffect(() => {
    setActiveIndex(0);
    setIsZoomed(false);
    setIsWishlisted(false);

    setZoomPosition({
      x: 50,
      y: 50,
    });

    if (mainSwiperRef.current) {
      mainSwiperRef.current.slideTo(0, 0);
    }

    if (thumbnailSwiperRef.current) {
      thumbnailSwiperRef.current.slideTo(0, 0);
    }
  }, [product]);

  /* =========================
     MAIN IMAGE CHANGE
  ========================= */

  const handleMainSlideChange = (swiper) => {
    const index = swiper.realIndex;

    setActiveIndex(index);

    setIsZoomed(false);

    /*
      Keep thumbnail swiper synchronized (one-way only: main -> thumbs).
      The thumbnail swiper must not drive the main swiper on its own
      slide change, otherwise both swipers keep triggering each other.
    */
    if (thumbnailSwiperRef.current && !thumbnailSwiperRef.current.destroyed) {
      thumbnailSwiperRef.current.slideTo(index);
    }
  };

  /* =========================
     THUMBNAIL CLICK
  ========================= */

  const handleThumbnailClick = (index) => {
    setActiveIndex(index);
    setIsZoomed(false);

    if (mainSwiperRef.current && !mainSwiperRef.current.destroyed) {
      mainSwiperRef.current.slideToLoop(index);
    }
  };

  /* =========================
     WISHLIST
  ========================= */

  const handleWishlist = (e) => {
    e.stopPropagation();

    setIsWishlisted((prev) => !prev);

    // Your wishlist API / Redux logic here
  };

  /* =========================
     ZOOM
  ========================= */

  const handleZoomClick = () => {
    setIsZoomed((prev) => !prev);

    if (!isZoomed) {
      setZoomPosition({
        x: 50,
        y: 50,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isZoomed) return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();

    let x = ((e.clientX - rect.left) / rect.width) * 100;
    let y = ((e.clientY - rect.top) / rect.height) * 100;

    x = Math.max(0, Math.min(100, x));
    y = Math.max(0, Math.min(100, y));

    setZoomPosition({
      x,
      y,
    });
  };

  const handleMouseLeave = () => {
    if (!isZoomed) return;

    setZoomPosition({
      x: 50,
      y: 50,
    });
  };

  if (!productImages.length) {
    return null;
  }

  return (
    <section className="Product_details_swiper">
      {/* =====================================================
          MAIN IMAGE SWIPER
      ===================================================== */}

      <div className="main_img_holder">
        <Swiper
          modules={[Autoplay, Navigation]}
          loop={productImages.length > 1}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          speed={700}
          navigation={{
            prevEl: ".main_image_arrow_left",
            nextEl: ".main_image_arrow_right",
          }}
          onSwiper={(swiper) => {
            mainSwiperRef.current = swiper;
          }}
          onSlideChange={handleMainSlideChange}
          className="main_product_swiper"
        >
          {productImages.map((item, index) => (
            <SwiperSlide key={item?.id || index}>
              <div
                className={`big_img_holder ${isZoomed ? "zoomed" : ""}`}
                onClick={handleZoomClick}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <img
                  src={process.env.REACT_APP_IMG_URL + item?.image}
                  alt={`Product ${index + 1}`}
                  className="Product_img"
                  style={{
                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                  }}
                />

                {/* WISHLIST */}

                {/* <button
                  type="button"
                  className={`product_wishlist_btn ${
                    isWishlisted ? "selected" : ""
                  }`}
                  onClick={handleWishlist}
                  aria-label="Wishlist"
                >
                  <FontAwesomeIcon
                    icon={
                      isWishlisted
                        ? faHeartSolid
                        : faHeartRegular
                    }
                  />
                </button> */}

                {/* ZOOM */}

                <div className="zoom_icon">
                  <FontAwesomeIcon
                    icon={
                      isZoomed ? faMagnifyingGlassMinus : faMagnifyingGlassPlus
                    }
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}

          {/* MAIN PREVIOUS */}

          {productImages.length > 1 && (
            <button
              type="button"
              className="main_image_arrow main_image_arrow_left"
              aria-label="Previous image"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          )}

          {/* MAIN NEXT */}

          {productImages.length > 1 && (
            <button
              type="button"
              className="main_image_arrow main_image_arrow_right"
              aria-label="Next image"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          )}
        </Swiper>
      </div>

      {/* =====================================================
          THUMBNAIL SWIPER
      ===================================================== */}

      <div className="thumbnail_wrapper">
        {/* THUMB PREVIOUS */}

        {/* <button
          type="button"
          className="thumbnail_nav thumbnail_nav_prev"
          aria-label="Previous thumbnail"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button> */}

        <Swiper
          modules={[Navigation]}
          onSwiper={(swiper) => {
            thumbnailSwiperRef.current = swiper;
          }}
          navigation={{
            prevEl: ".thumbnail_nav_prev",
            nextEl: ".thumbnail_nav_next",
          }}
          watchSlidesProgress
          loop={false}
          slidesPerView={4}
          spaceBetween={8}
          speed={500}
          breakpoints={{
            0: {
              slidesPerView: 4,
              spaceBetween: 8,
            },
            576: {
              slidesPerView: 5,
              spaceBetween: 10,
            },
            768: {
              slidesPerView: 4,
              spaceBetween: 10,
            },
            992: {
              slidesPerView: 5,
              spaceBetween: 10,
            },
            1200: {
              slidesPerView: 5,
              spaceBetween: 12,
            },
          }}
          className="product_thumbnail_swiper"
        >
          {productImages.map((item, index) => (
            <SwiperSlide
              key={item?.id || index}
              onClick={() => handleThumbnailClick(index)}
            >
              <div
                className={`img_holder ${
                  activeIndex === index ? "active_thumb" : ""
                }`}
              >
                <img
                  src={process.env.REACT_APP_IMG_URL + item?.image}
                  alt={`Product thumbnail ${index + 1}`}
                  className="Product_img"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* THUMB NEXT */}

        {/* <button
          type="button"
          className="thumbnail_nav thumbnail_nav_next"
          aria-label="Next thumbnail"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button> */}
      </div>
    </section>
  );
}

export default Product_details_swiper;

// import React, { useEffect, useRef, useState } from "react";
// import "./Product_details_swiper.css";
// import { Swiper, SwiperSlide } from "swiper/react";
// import "swiper/css";

// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faAngleLeft,
//   faAngleRight,
//   faMagnifyingGlassPlus,
//   faMagnifyingGlassMinus,
// } from "@fortawesome/free-solid-svg-icons";

// import { FreeMode, Navigation } from "swiper/modules";

// function Product_details_swiper({ product }) {
//   const [activeImage, setActiveImage] = useState("");

//   const [isZoomed, setIsZoomed] = useState(false);

//   const [zoomPosition, setZoomPosition] = useState({
//     x: 50,
//     y: 50,
//   });

//   const prevRef = useRef(null);
//   const nextRef = useRef(null);
//   const imageContainerRef = useRef(null);

//   // Set first product image when product data loads
//   useEffect(() => {
//     if (product) {
//       const firstImage =
//         product?.image || product?.Product_Images?.[0]?.image || "";

//       setActiveImage(firstImage);

//       setIsZoomed(false);

//       setZoomPosition({
//         x: 50,
//         y: 50,
//       });
//     }
//   }, [product]);

//   // Thumbnail click
//   const handleThumbnailClick = (image) => {
//     if (!image) return;

//     setActiveImage(image);

//     // Reset zoom when changing image
//     setIsZoomed(false);

//     setZoomPosition({
//       x: 50,
//       y: 50,
//     });
//   };

//   // Zoom click
//   const handleZoomClick = () => {
//     setIsZoomed((prev) => !prev);

//     if (!isZoomed) {
//       setZoomPosition({
//         x: 50,
//         y: 50,
//       });
//     }
//   };

//   // Move zoom position
//   const handleMouseMove = (e) => {
//     if (!isZoomed || !imageContainerRef.current) return;

//     const rect = imageContainerRef.current.getBoundingClientRect();

//     let x = ((e.clientX - rect.left) / rect.width) * 100;
//     let y = ((e.clientY - rect.top) / rect.height) * 100;

//     x = Math.max(0, Math.min(100, x));
//     y = Math.max(0, Math.min(100, y));

//     setZoomPosition({
//       x,
//       y,
//     });
//   };

//   // Reset zoom position
//   const handleMouseLeave = () => {
//     if (!isZoomed) return;

//     setZoomPosition({
//       x: 50,
//       y: 50,
//     });
//   };

//   return (
//     <>
//       <section className="Product_details_swiper">
//         <div className="two_divs_holder">
//           {/* THUMBNAILS */}
//           <div className="div1">
//             <div className="swiper_holder">
//               {/* Right Arrow */}
//               <div ref={prevRef} className="custom_arrow right">
//                 <FontAwesomeIcon icon={faAngleRight} />
//               </div>

//               {/* Left Arrow */}
//               <div ref={nextRef} className="custom_arrow left">
//                 <FontAwesomeIcon icon={faAngleLeft} />
//               </div>

//               <Swiper
//                 direction="vertical"
//                 slidesPerView={5}
//                 spaceBetween={12}
//                 modules={[FreeMode, Navigation]}
//                 freeMode={true}
//                 navigation={{
//                   prevEl: prevRef.current,
//                   nextEl: nextRef.current,
//                 }}
//                 onBeforeInit={(swiper) => {
//                   swiper.params.navigation.prevEl = prevRef.current;
//                   swiper.params.navigation.nextEl = nextRef.current;
//                 }}
//                 className="thumb-swiper"
//                 breakpoints={{
//                   0: {
//                     direction: "horizontal",
//                     slidesPerView: 4,
//                     spaceBetween: 8,
//                   },
//                   576: {
//                     direction: "horizontal",
//                     slidesPerView: 6,
//                     spaceBetween: 10,
//                   },
//                   768: {
//                     direction: "horizontal",
//                     slidesPerView: 4,
//                     spaceBetween: 10,
//                   },
//                   992: {
//                     direction: "horizontal",
//                     slidesPerView: 5,
//                     spaceBetween: 9,
//                   },
//                   1200: {
//                     direction: "vertical",
//                     slidesPerView: 4,
//                     spaceBetween: 12,
//                   },
//                   1400: {
//                     direction: "vertical",
//                     slidesPerView: 4,
//                     spaceBetween: 12,
//                   },
//                 }}
//               >
//                 {product?.Product_Images?.map((val, index) => (
//                   <SwiperSlide
//                     key={val?.id || index}
//                     onClick={() => handleThumbnailClick(val?.image)}
//                   >
//                     <div
//                       className={`img_holder ${
//                         activeImage === val?.image ? "active_thumb" : ""
//                       }`}
//                     >
//                       <img
//                         className="Product_img"
//                         src={process.env.REACT_APP_IMG_URL + val?.image}
//                         alt="Product_img"
//                       />
//                     </div>
//                   </SwiperSlide>
//                 ))}
//               </Swiper>
//             </div>
//           </div>

//           {/* MAIN IMAGE */}
//           <div className="div2">
//             <div className="main_img_holder">
//               <div
//                 ref={imageContainerRef}
//                 className={`big_img_holder ${isZoomed ? "zoomed" : ""}`}
//                 onClick={handleZoomClick}
//                 onMouseMove={handleMouseMove}
//                 onMouseLeave={handleMouseLeave}
//               >
//                 {activeImage && (
//                   <img
//                     className="Product_img"
//                     src={process.env.REACT_APP_IMG_URL + activeImage}
//                     alt="Product_img"
//                     style={{
//                       transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
//                     }}
//                   />
//                 )}

//                 {/* Zoom Icon */}
//                 <div className="zoom_icon">
//                   <FontAwesomeIcon
//                     icon={
//                       isZoomed ? faMagnifyingGlassMinus : faMagnifyingGlassPlus
//                     }
//                   />
//                 </div>

//                 {/* Zoom Text */}
//                 <div className="zoom_text">
//                   {isZoomed ? "Click to reset" : "Click to zoom"}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>
//     </>
//   );
// }

// export default Product_details_swiper;
